import { Api } from '@/Api';
import { defineStore } from 'pinia';
import { REQUEST_CONTENT_STATUS, computeStatus, type RequestContentStatus } from '@/modules/dashboard/requestContents/constants/constants';
import { useDebug, DEBUG_NAMESPACE } from '@/composables/useDebug';
import { useSettingsStore } from './useSettingsStore';

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_REQUEST_CONTENT);

// Enhanced types from the module store
export interface RequestContent {
  id: string;
  name: string;
  slug: string;
  customUrl?: string;
  message?: string;
  customId?: string;
  expiresAt?: string;
  status: RequestContentStatus;
  createdAt: string;
  updatedAt: string;
  contentType?: 'video' | 'file' | 'image';
  collectLogs?: boolean;
  logCollectionDomain?: string;
  collectionId?: string;
  collectionName?: string; // Collection name from API included data
  items: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  requestLink?: string;
}

export interface RequestContentListResponse {
  data: RequestContent[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    hasHiddenReplies?: boolean;
    monthlyReplyLimit?: number | null;
  };
}

export interface RequestContentSettings {
  defaultTitle: string;
  defaultDescription: string;
}

// Legacy interface for backwards compatibility
export interface RequestContentItem {
  id: string;
  slug?: string;
  name: string;
  message?: string;
  customId?: string;
  requestLink?: string;
  collectionId?: string;
}

export interface RequestContentState {
  // Enhanced state structure
  requestContents: RequestContent[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    hasHiddenReplies: boolean;
    // null = no finite cap (unlimited / disabled / not yet loaded — they collapse for FE purposes).
    // -1 = unlimited (BE sentinel, pre-SW-6449); 0 = disabled; positive = finite cap.
    // Consumers should switch on `hasReplyLimit` rather than reading the raw value.
    monthlyReplyLimit: number | null;
  };
  settings: RequestContentSettings;
  isLoading: boolean;
  error: string | null;
  // Individual item loading states
  itemLoadingStates: Record<string, boolean>;
  // Populated via parseRequestContentFromApi (loadById/loadAll), which always
  // returns the richer RequestContent shape — typed accordingly so consumers
  // (e.g. useRequestContentInfoPage) get status/createdAt/etc. without a cast.
  list: RequestContent[];
  loading: boolean;
}

export const useRequestContentStore = defineStore('requestContentStore', {
  state: (): RequestContentState => ({
    // Enhanced state
    requestContents: [],
    meta: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      perPage: 20,
      hasHiddenReplies: false,
      monthlyReplyLimit: null,
    },
    settings: {
      defaultTitle: '',
      defaultDescription: '',
    },
    isLoading: false,
    error: null,
    // Individual item loading states
    itemLoadingStates: {},
    // Legacy support
    list: [],
    loading: false,
  }),
  actions: {
    // Parse backend request content response to frontend format
    parseRequestContentFromApi(apiItem: any): RequestContent {
      const status = computeStatus(apiItem.attributes.status, apiItem.attributes.expiresAt);
      const collectionId = apiItem.relationships?.collection?.data?.id;

      const parsed = {
        id: apiItem.id,
        name: apiItem.attributes.name,
        slug: apiItem.attributes.slug,
        customUrl: apiItem.attributes.customUrl,
        message: apiItem.attributes.message,
        customId: apiItem.attributes.customId,
        expiresAt: apiItem.attributes.expiresAt,
        status,
        createdAt: apiItem.attributes.createdAt,
        updatedAt: apiItem.attributes.updatedAt,
        contentType: apiItem.attributes.contentType || 'video',
        collectLogs: apiItem.attributes.collectLogs,
        logCollectionDomain: apiItem.attributes.logCollectionDomain,
        collectionId,
        collectionName: apiItem.attributes.collectionName, // Now comes directly from serializer attributes
        items: apiItem.relationships?.items?.data || [],
        requestLink: apiItem.links?.requestLink,
      };

      return parsed;
    },

    // Enhanced loadRequestContents method using real API
    async loadRequestContents() {
      this.setLoadingState(true);

      try {
        const response = await this.fetchRequestContents();
        this.processRequestContentsResponse(response);
      } catch (error) {
        this.handleLoadError(error);
        throw error;
      } finally {
        this.setLoadingState(false);
      }
    },

    async fetchRequestContents() {
      const response = await Api.requestContent.list({
        page: this.meta.currentPage,
        perPage: this.meta.perPage,
      });
      return response;
    },

    processRequestContentsResponse(response: any) {
      if (!response || !response.data?.requestContents) {
        this.handleEmptyResponse();
        return;
      }

      this.updateRequestContents(response.data.requestContents);
      // All metadata comes from response.meta for consistency
      const meta = response.meta || {};
      this.updatePaginationMeta(meta.totalCount, meta.hasHiddenReplies, meta.monthlyReplyLimit);
    },

    updateRequestContents(items: any[]) {
      this.requestContents = items.map((item: any) => this.parseRequestContentFromApi(item));
    },

    updatePaginationMeta(totalCount: number, hasHiddenReplies?: boolean, monthlyReplyLimit?: number | null) {
      const count = totalCount || this.requestContents.length;
      const totalPages = Math.ceil(count / this.meta.perPage);

      this.meta = {
        currentPage: this.meta.currentPage,
        totalPages,
        totalCount: count,
        perPage: this.meta.perPage,
        hasHiddenReplies: hasHiddenReplies || false,
        monthlyReplyLimit: monthlyReplyLimit ?? null,
      };
    },

    handleEmptyResponse() {
      this.requestContents = [];
      this.list = [];
      this.meta = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        perPage: 20,
        hasHiddenReplies: false,
        monthlyReplyLimit: null,
      };
    },

    handleLoadError(error: any) {
      this.error = error instanceof Error ? error.message : 'Failed to load request contents';
      debug('[RequestContent] Cannot load request content list', error);
    },

    setLoadingState(loading: boolean) {
      this.isLoading = loading;
      this.loading = loading; // for legacy compatibility
      if (loading) {
        this.error = null;
      }
    },

    async changePage(page: number) {
      if (page < 1 || page > this.meta.totalPages) {
        return;
      }
      this.meta.currentPage = page;
      await this.loadRequestContents();
    },

    async createRequestContent(formData: any) {
      this.isLoading = true;
      this.error = null;

      try {
        const { data } = await Api.requestContent.create(formData);

        const createdItem = this.parseRequestContentFromApi(data.requestContent);

        this.requestContents.unshift(createdItem);
        this.meta.totalCount++;

        return createdItem;
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to create request content';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateRequestContent(itemId: string, formData: any) {
      // Set loading state for specific item
      this.itemLoadingStates[itemId] = true;
      this.error = null;

      debug('Updating request content with ID:', itemId);
      debug('Update payload:', formData);

      try {
        const { data } = await Api.requestContent.update(itemId, formData);
        debug('Update successful, response:', data);

        const updatedItem = this.parseRequestContentFromApi(data.requestContent);

        const index = this.requestContents.findIndex(rc => rc.id === itemId);
        if (index > -1) {
          this.requestContents[index] = updatedItem;
        }

        return updatedItem;
      } catch (error: any) {
        debug('Update failed with error:', error);
        this.error = error instanceof Error ? error.message : 'Failed to update request content';
        throw error;
      } finally {
        delete this.itemLoadingStates[itemId];
      }
    },

    async deleteRequestContent(item: RequestContent) {
      // Set loading state for specific item
      this.itemLoadingStates[item.id] = true;
      this.error = null;

      try {
        await Api.requestContent.delete(item.id);

        const index = this.requestContents.findIndex(rc => rc.id === item.id);
        if (index > -1) {
          this.requestContents.splice(index, 1);
          this.meta.totalCount--;

          // Update legacy format
          const legacyIndex = this.list.findIndex(rc => rc.id === item.id);
          if (legacyIndex > -1) {
            this.list.splice(legacyIndex, 1);
          }
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to delete request content';
        throw error;
      } finally {
        delete this.itemLoadingStates[item.id];
      }
    },

    async toggleStatus(item: RequestContent) {
      const originalStatus = item.status;
      const originalExpiresAt = item.expiresAt;

      // Determine new status and expiration handling - match old system format
      const newStatus = item.status === REQUEST_CONTENT_STATUS.ACTIVE ? REQUEST_CONTENT_STATUS.INACTIVE : REQUEST_CONTENT_STATUS.ACTIVE;
      const updatePayload: any = {
        status: newStatus, // Send string "active"/"inactive" like old system
        id: item.id,
        expires_at: 0, // Default value like old system
      };

      // If turning on an expired item, clear expiration date
      if (item.status === REQUEST_CONTENT_STATUS.EXPIRED && newStatus === REQUEST_CONTENT_STATUS.ACTIVE) {
        updatePayload.expires_at = null;
      }

      // Set loading state for specific item
      this.itemLoadingStates[item.id] = true;

      try {
        // Optimistic update
        item.status = newStatus;
        if (updatePayload.expires_at === null) {
          item.expiresAt = undefined;
        }

        // API call with only the status field that changed
        await this.updateRequestContent(item.id, updatePayload);

        return { success: true, message: `Request link ${newStatus === REQUEST_CONTENT_STATUS.ACTIVE ? 'enabled' : 'disabled'} successfully!` };
      } catch (apiError) {
        // Revert optimistic update on error
        item.status = originalStatus;
        item.expiresAt = originalExpiresAt;

        debug('Failed to update status:', apiError);
        return { success: false, message: 'Failed to update request link status. Please try again.' };
      } finally {
        this.isLoading = false;
      }
    },

    async checkCustomUrlAvailability(customUrl: string, excludeId?: string) {
      try {
        await Api.requestContent.checkCustomUrl({
          custom_url: customUrl,
          id: excludeId,
        });
        // If the API call succeeds, the URL is available
        return true;
      } catch (error) {
        // If the API call fails, the URL is not available or invalid
        debug('URL not available:', error);
        return false;
      }
    },

    // Load a single request content by ID (used by viewer module)
    async loadById(requestContentId: string) {
      this.loading = true;
      this.error = null;

      try {
        const { data } = await Api.requestContent.get(requestContentId);

        if (data?.requestContent) {
          const item = this.parseRequestContentFromApi(data.requestContent);

          // Update the list with the single item for viewer compatibility
          this.list = [item];

          // Also add to requestContents if not already present
          const existingIndex = this.requestContents.findIndex(rc => rc.id === item.id);
          if (existingIndex === -1) {
            this.requestContents.push(item);
          } else {
            this.requestContents[existingIndex] = item;
          }

          return item;
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to load request content';
        debug('[RequestContent] Cannot load request content item:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    clearError() {
      this.error = null;
    },

    resetState() {
      this.requestContents = [];
      this.meta = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        perPage: 20,
        hasHiddenReplies: false,
        monthlyReplyLimit: null,
      };
      this.settings = {
        defaultTitle: '',
        defaultDescription: '',
      };
      this.isLoading = false;
      this.error = null;
      // Reset legacy state
      this.list = [];
      this.loading = false;
    },
  },
  getters: {
    // Enhanced getters
    totalItems: state => state.meta.totalCount,
    hasHiddenReplies: state => state.meta.hasHiddenReplies,
    monthlyReplyLimit: state => state.meta.monthlyReplyLimit,
    hasReplyLimit: state => !!state.meta.monthlyReplyLimit && state.meta.monthlyReplyLimit > 0,
    hasMultiplePages: state => state.meta.totalPages > 1,
    isFirstPage: state => state.meta.currentPage === 1,
    isLastPage: state => state.meta.currentPage === state.meta.totalPages,
    // Domain getter that uses custom domain if available, otherwise falls back to state domain
    domain: () => {
      const settingsStore = useSettingsStore();
      return settingsStore.effectiveDomain || '';
    },
    // Item-specific loading state
    isItemLoading: state => {
      return (itemId: string) => state.itemLoadingStates[itemId] || false;
    },
  },
});
